import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { NgbDatepickerI18n, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { HttpClientModule } from '@angular/common/http';
import { InlineSVGModule } from 'ng-inline-svg';

import { NumberStatusComponent } from './number-status/number-status.component';
import { HotelReservationDetailsComponent } from './hotel-reservation-details/hotel-reservation-details.component';
import { TitleComponent } from './title/title.component';
import { ResDirectComponent } from './res-direct/res-direct.component';
import { NightsGuestsComponent } from './nights-guests/nights-guests.component';
import { CancelDatesComponent } from './cancel-dates';
import { HotelInfoComponent } from './hotel-info';
import { CancelResButtonComponent } from './cancel-res-button';

import {
  CleanPhoneNumberPipe,
  DateFormatterPipe,
  MessageArgumentsPipe,
  SafePipe
} from '../common/pipe';

import { FrontDeskComponent } from './front-desk';
import { MessagesComponent } from './messages';
import { EnquiryService, MediaQueryService } from '../common/media-query';
import { RedirectorComponent } from './redirector';
import { CheckboxComponent } from './checkbox';
import { SpinnerComponent } from './spinner';
import { DoNotCancelButtonComponent } from './do-not-cancel-button';
import { PoliciesComponent } from './policies';
import { ViewMyAccountComponent } from './view-my-account';
import { MakeAnotherReservationComponent } from './make-another-reservation';
import { BpgImageComponent } from './bpg-image';
import { BaseAccordionComponent } from './base-accordion';
import { ModifyStayDetailsComponent } from './modify-stay-details';
import { NumberSelectorComponent } from './number-selector';
import { DateRangeSelectorComponent } from './date-range-selector';
import { AddGuestsComponent } from './add-guests';
import { ModifyRoomComponent } from './modify-room';
import { ModifyRateComponent } from './modify-rate';
import { HotelAddressComponent } from './hotel-address';
import { HotelBrandInfoComponent } from './hotel-brand-info';
import { HotelImageComponent } from './hotel-image';
import { CurrencyConverterComponent } from './currency-converter';
import { RateDetailsComponent } from './rate-details/rate-details.component';
import { AlertMessageComponent } from './alert-message';
import { CustomDatePickerComponent } from './custom-datepicker/custom-datepicker.component';
import { CustomDatepickerMonthComponent } from './custom-datepicker/custom-datepicker-month.component';
import { CustomDatepickerNavigationComponent } from './custom-datepicker/custom-datepicker-navigation.component';
import { CheckinCheckoutDatesComponent } from './checkin-checkout-dates';
import { NgbDatepickerI18nCustomService } from './custom-datepicker/service/datepicker-i18n-custom.service';
import { ReservationPoliciesComponent } from './reservation-policies';
import { ReservationChangesSummaryComponent } from './reservation-changes-summary/reservation-changes-summary.component';
import { PreviewSummaryButtonComponent } from './preview-summary-button';
import { NgxPageScrollModule } from 'ngx-page-scroll';
import { GroupCodeComponent } from './group-code';
import { PriceAmountComponent } from './price-amount/price-amount.component';
import { DepositPolicyComponent } from './deposit-policy/deposit-policy.component';
import { RateRuleComponent } from './rate-rule/rate-rule.component';
import { FixedSummaryContainerComponent } from './fixed-summary-container';

import { StayPreferenceComponent } from './stay-preference/stay-preference.component';

import { ConfirmationNumberComponent } from './confirmation-number/confirmation-number.component';
import { BrandLogoComponent } from './brand-logo/brand-logo.component';
import { PointsEstimationComponent } from './points-estimation';
import { PointsDescriptionComponent } from './points-description';
import { RoomStayPreferencesComponent } from './room-stay-preferences';
import { UserReservationCheckComponent } from './user-reservation-check';
import { ReservationLoginRequiredCheckComponent } from './reservation-login-required-check';
import { DynamicMessageComponent } from './dynamic-message/dynamic-message.component';
import { VirtualAssistantComponent } from './virtual-assistant';
import { LiveChatComponent } from './live-chat';
import { CancellationPolicyComponent } from './cancellation-policy/cancellation-policy.component';
import { HotelReservationSearchComponent } from './hotel-reservation-search/hotel-reservation-search.component';
import { CarReservationSearchComponent } from './car-reservation-search/car-reservation-search.component';
import { WriteReviewHotelInfoComponent } from './write-review-hotel-info/write-review-hotel-info.component';

import { ContentHeightDirective } from '../common/directives';
import { WhenActionProgressDirective } from './when-action-progress/when-action-progress';
import { OpenWalletDirective } from './open-wallet/open-wallet.directive';
import { OpenVirtualAssistantDirective } from './open-virtual-assistant/open-virtual-assistant.directive';
import { ReservationAssociationComponent } from './reservation-association/reservation-association.component';

import { OpenAccountManagementDirective } from './open-account-management/open-account-management.directive';
import { PastDateRangeSelector } from './past-date-range-selector/past-date-range-selector';

@NgModule({
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    NgbModule,
    HttpClientModule,
    InlineSVGModule.forRoot({baseUrl: `/resources/global/stay-mgmt/v12.0.0/assets/svg/`}),
    NgxPageScrollModule
  ],
  declarations: [
    ResDirectComponent,
    TitleComponent,
    NumberStatusComponent,
    HotelReservationDetailsComponent,
    NightsGuestsComponent,
    CancelDatesComponent,
    HotelInfoComponent,
    FrontDeskComponent,
    CleanPhoneNumberPipe,
    MessageArgumentsPipe,
    DateFormatterPipe,
    SafePipe,
    MessagesComponent,
    RedirectorComponent,
    CheckboxComponent,
    SpinnerComponent,
    CancelResButtonComponent,
    DoNotCancelButtonComponent,
    PoliciesComponent,
    ViewMyAccountComponent,
    MakeAnotherReservationComponent,
    BpgImageComponent,
    BaseAccordionComponent,
    ModifyStayDetailsComponent,
    NumberSelectorComponent,
    DateRangeSelectorComponent,
    AddGuestsComponent,
    ModifyRoomComponent,
    ModifyRateComponent,
    HotelBrandInfoComponent,
    HotelImageComponent,
    HotelAddressComponent,
    CurrencyConverterComponent,
    RateDetailsComponent,
    AlertMessageComponent,
    CustomDatePickerComponent,
    CustomDatepickerMonthComponent,
    CustomDatepickerNavigationComponent,
    CheckinCheckoutDatesComponent,
    ReservationChangesSummaryComponent,
    WhenActionProgressDirective,
    ContentHeightDirective,
    OpenAccountManagementDirective,
    ReservationPoliciesComponent,
    PreviewSummaryButtonComponent,
    GroupCodeComponent,
    PriceAmountComponent,
    DepositPolicyComponent,
    RateRuleComponent,
    FixedSummaryContainerComponent,
    RoomStayPreferencesComponent,
    StayPreferenceComponent,
    ConfirmationNumberComponent,
    BrandLogoComponent,
    DynamicMessageComponent,
    OpenWalletDirective,
    UserReservationCheckComponent,
    PointsEstimationComponent,
    PointsDescriptionComponent,
    CancellationPolicyComponent,
    ReservationLoginRequiredCheckComponent,
    HotelReservationSearchComponent,
    CarReservationSearchComponent,
    ReservationAssociationComponent,
    OpenVirtualAssistantDirective,
    VirtualAssistantComponent,
    LiveChatComponent,
    WriteReviewHotelInfoComponent,
    PastDateRangeSelector
  ],
  exports: [
    ResDirectComponent,
    TitleComponent,
    NumberStatusComponent,
    HotelReservationDetailsComponent,
    NightsGuestsComponent,
    CancelDatesComponent,
    HotelInfoComponent,
    FrontDeskComponent,
    CleanPhoneNumberPipe,
    MessageArgumentsPipe,
    DateFormatterPipe,
    MessagesComponent,
    RedirectorComponent,
    CheckboxComponent,
    SpinnerComponent,
    CancelResButtonComponent,
    DoNotCancelButtonComponent,
    PoliciesComponent,
    ViewMyAccountComponent,
    MakeAnotherReservationComponent,
    BpgImageComponent,
    BaseAccordionComponent,
    ModifyStayDetailsComponent,
    NumberSelectorComponent,
    DateRangeSelectorComponent,
    AddGuestsComponent,
    ModifyRoomComponent,
    ModifyRateComponent,
    HotelBrandInfoComponent,
    HotelImageComponent,
    HotelAddressComponent,
    CurrencyConverterComponent,
    HotelAddressComponent,
    RateDetailsComponent,
    AlertMessageComponent,
    HotelAddressComponent,
    CustomDatePickerComponent,
    CustomDatepickerMonthComponent,
    CustomDatepickerNavigationComponent,
    CheckinCheckoutDatesComponent,
    ReservationChangesSummaryComponent,
    WhenActionProgressDirective,
    ContentHeightDirective,
    OpenAccountManagementDirective,
    ReservationPoliciesComponent,
    PreviewSummaryButtonComponent,
    GroupCodeComponent,
    PriceAmountComponent,
    DepositPolicyComponent,
    RateRuleComponent,
    FixedSummaryContainerComponent,
    RoomStayPreferencesComponent,
    StayPreferenceComponent,
    ConfirmationNumberComponent,
    BrandLogoComponent,
    UserReservationCheckComponent,
    OpenWalletDirective,
    PointsEstimationComponent,
    PointsDescriptionComponent,
    CancellationPolicyComponent,
    ReservationLoginRequiredCheckComponent,
    HotelReservationSearchComponent,
    CarReservationSearchComponent,
    ReservationAssociationComponent,
    OpenVirtualAssistantDirective,
    VirtualAssistantComponent,
    LiveChatComponent,
    WriteReviewHotelInfoComponent,
    PastDateRangeSelector,
  ],
  providers: [
    { provide: NgbDatepickerI18n, useClass: NgbDatepickerI18nCustomService },
    MediaQueryService,
    EnquiryService
  ]
})
export class ComponentsModule {
}



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/components/components.module.ts